/**
 * Formats a date for display. Date-only strings and `calendarDay` values are
 * calendar days stored as UTC midnight, so they are formatted in UTC to avoid
 * showing the previous day west of UTC.
 */
export function formatDate(
	dateInput?: string | Date | null,
	{ calendarDay = false }: { calendarDay?: boolean } = {},
) {
	if (!dateInput) return '–'

	if (typeof dateInput === 'string') {
		const [year, month, day] = dateInput.split('-')

		if (month === '00') return year

		if (day === '00') {
			return new Intl.DateTimeFormat('en-US', {
				month: 'short',
				year: 'numeric',
			}).format(new Date(Number(year), Number(month) - 1))
		}
	}

	const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput

	if (Number.isNaN(date.getTime())) return '–'

	const isDateOnly =
		typeof dateInput === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateInput)
	return new Intl.DateTimeFormat('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		...(calendarDay || isDateOnly ? { timeZone: 'UTC' } : {}),
	}).format(date)
}

/** Calendar-free duration units shared by collection and Activity dates. */
export const DURATION_UNITS = [
	{ ms: 365 * 24 * 60 * 60 * 1000, unit: 'year', short: 'y' },
	{ ms: 30 * 24 * 60 * 60 * 1000, unit: 'month', short: 'mo' },
	{ ms: 24 * 60 * 60 * 1000, unit: 'day', short: 'd' },
] as const

export function getDuration(date: string | Date, short: boolean): string {
	const now = new Date()
	const diffMs = new Date(date).getTime() - now.getTime()

	for (const { ms, unit, short: shortUnit } of DURATION_UNITS) {
		const name = short ? shortUnit : unit
		const value = Math.floor(diffMs / ms)

		if (value >= 1) {
			return short
				? `${value} ${name}`
				: `${value} ${name}${value > 1 ? 's' : ''}`
		}
	}

	return 'today'
}

export function formatRelativeTime(value: string | Date): string {
	const date = new Date(value)
	const seconds = Math.round((date.getTime() - Date.now()) / 1000)
	const formatter = new Intl.RelativeTimeFormat(undefined, {
		numeric: 'auto',
	})
	const ranges: Array<[Intl.RelativeTimeFormatUnit, number]> = [
		...DURATION_UNITS.map(
			({ unit, ms }): [Intl.RelativeTimeFormatUnit, number] => [
				unit,
				ms / 1000,
			],
		),
		['hour', 3600],
		['minute', 60],
	]
	for (const [unit, size] of ranges) {
		if (Math.abs(seconds) >= size)
			return formatter.format(Math.trunc(seconds / size), unit)
	}
	return formatter.format(seconds, 'second')
}

/**
 * Return a friendly "last heard" string from a list of ISO date strings or Date objects.
 */
export function getFormattedLastHeardFromHistoryAsString(
	historyDates: (string | Date)[],
): string {
	if (!historyDates || historyDates.length === 0) return ''
	const last = new Date(
		Math.max(
			...historyDates.map((d) => {
				const date = typeof d === 'string' ? new Date(d) : d
				return date.getTime()
			}),
		),
	)
	return formatDate(last, { calendarDay: true })
}

export function toIsoDateString(date: string | Date): string {
	return new Date(date).toISOString().split('T')[0]
}
